import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Planbook } from "@shared/models/planbook"
import { isApple } from "@shared/unified-tiptap/util/is-apple"
import copyPlanbookLinkAction from "cc-frontend/lib/actions/app-state/COPY_PLANBOOK_LINK"
import {
    createQrCode,
    createShareLink,
    describeShareLink,
    perspectiveFromPathname,
} from "cc-frontend/lib/share-link"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Session from "cc-frontend/services/session"
import { tracked } from "tracked-built-ins"
import styles from "./public-link.module.css"

interface Sig {
  Args: {
    options: {
      planbook: Planbook
      /** Replaces the part after /planbook/<planbookId> */
      overridePath?: string
      overrideDateString?: string
    }
    accept: () => void
    decline: () => void
  }
}

export default class PublicLink extends Component<Sig> {
  readonly styles = styles

  @service declare modalManager: ModalManager
  @service declare session: Session
  @service declare dispatcher: Dispatcher
  @service notifier

  @tracked hideNav = false

  constructor(owner, args) {
    super(owner, args)

    if (this.args.options.overridePath) this.hideNav = true
  }

  get pathname(): string {
    if (this.args.options.overridePath) {
      return window.location.pathname.replace(
        /^(\/planbooks\/[^/]+)\/.*$/,
        `$1${this.args.options.overridePath}`
      )
    } else return window.location.pathname
  }

  get dateString(): string | null {
    if (this.args.options.overrideDateString) {
      return this.args.options.overrideDateString
    } else {
      const queryParams = new URLSearchParams(window.location.search)
      return queryParams.get("date")
    }
  }

  get shareLink() {
    return createShareLink(this.pathname, new URLSearchParams(window.location.search), {
      hideNav: this.hideNav,
      dateString: this.dateString,
    })
  }

  get movementDescription(): string {
    switch (perspectiveFromPathname(this.pathname)) {
      case "day":
        return "from day to day"
      case "week":
        return "from week to week"
      case "month":
        return "from month to month"
      default:
        return "between pages"
    }
  }

  get pageDescription() {
    return describeShareLink(this.pathname, { dateString: this.dateString })
  }

  @action
  setHideNav(value: boolean) {
    this.hideNav = value
  }

  @action
  emailLink() {
    // Close this dialog and open the email modal, in a setTimeout to make the animation look nicer.
    this.args.accept()

    setTimeout(() => {
      this.modalManager.openModal("planner/menus/parts/email-link-form", {
        pathname: this.pathname,
        baseUrlParams: new URLSearchParams(window.location.search),
        hideNav: this.hideNav,
        dateString: this.dateString,
        courseTitle: null,
        planbook: this.args.options.planbook,
        user: this.session.user.content,
      })
    })
  }

  @action
  showCopyToast() {
    this.notifier.success("", {
      title: `Your URL has been Copied!`,
      message: `Press ${isApple ? "Command(⌘) + v" : "Control + v"} to paste.`,
      duration: 6000,
    })
    this.trackAnalytics("link")

    // Don't close in case they want to click other buttons.
    // this.args.accept()
  }

  @action
  async downloadQrCode(e: MouseEvent) {
    const qr = createQrCode(this.shareLink)
    // qr.download doesn't work in this component because it uses an A click,
    // which is blocked by the dialog's focus trap.
    // Copy the relevant code here but put the A inside the dialog.
    // https://github.com/kozakdenys/qr-code-styling/blob/43d41710221204858ccbd5b5065ec41d23b50544/src/core/QRCodeStyling.ts#L217
    const element = await qr._getElement("png")
    if (!element) {
      return
    }
    const url = (element as HTMLCanvasElement).toDataURL("image/png")
    downloadURI(
      url,
      `${this.args.options.planbook.attributes.title || "Common Planner"} QR Code`,
      e.target as HTMLElement
    )

    this.trackAnalytics("qr_code")
  }

  private trackAnalytics(copied: "link" | "qr_code") {
    if (this.session.user.content) {
      this.dispatcher.dispatchPreparedAction(
        copyPlanbookLinkAction({
          userId: this.session.user.content.id,
          planbookId: this.args.options.planbook.id,
          planbookTitle: this.args.options.planbook.attributes.title,
          perspective: perspectiveFromPathname(this.pathname),
          copied,
          url: this.shareLink,
          shareScope: this.hideNav ? "current_view" : "planbook",
        })
      )
    }
  }
}

function downloadURI(uri: string, name: string, container: HTMLElement): void {
  const link = document.createElement("a")
  link.download = name
  link.href = uri
  container.appendChild(link)
  link.click()
  container.removeChild(link)
}
